import { NestedDownloadData } from "@nova/component-export-download-buttons-hover"
import * as Icons from "@nova/component-icons"
import { NovaSpinner } from "@nova/component-nova-spinner"
import classNames from "classnames"
import { animationClass } from "features/data-management/workspace/constants"
import { usePaywalls } from "features/entity-caching/licenses/useLicensePaywall"
import { RadioButton } from "@nova/ds-component-radio-button"
import { useAppSelector } from "integrations/redux/hooks"
import { Fragment } from "react"
import { selectIsDomainWhiteListed, selectUserLicense } from "user/profile/selectors"

export const InspectorPanelAccordionExportFileOptions = ({
    downloadOptions,
    depth = 0,
    prefix = "",
    selectedExportOptions,
    addExportOption,
    removeExportOption,
    closedCategories,
    setCategoryIsClosed,
    exportIdBeingCopied,
}: {
    downloadOptions: NestedDownloadData
    depth?: number
    prefix?: string
    selectedExportOptions: Set<string>
    addExportOption: (option: string) => void
    removeExportOption: (option: string) => void
    closedCategories: Set<string>
    setCategoryIsClosed: (category: string, isClosed: boolean) => void
    exportIdBeingCopied?: string
}) => {
    const userLicense = useAppSelector(selectUserLicense)
    const isWhitelisted = useAppSelector(selectIsDomainWhiteListed)
    const { showLicensePaywallModal } = usePaywalls()
    return (
        <div>
            {Object.entries(downloadOptions).map(([option, val]) =>
                typeof val.callback === "function" ? (
                    // Leaf download option with checkbox.
                    <div
                        key={option}
                        className="flex items-center gap-2 my-2.5 select-none cursor-pointer"
                        style={{ marginLeft: `${depth + 0.5}rem` }}
                        onClick={() =>
                            selectedExportOptions.has(prefix + option)
                                ? removeExportOption(prefix + option)
                                : addExportOption(prefix + option)
                        }
                    >
                        {/* Using radio buttons for now until we support multi-select for exporting. */}
                        {/* <Checkbox
                            isChecked={selectedExportOptions.has(prefix + option)}
                            onClick={() =>
                                selectedExportOptions.has(prefix + option)
                                    ? removeExportOption(prefix + option)
                                    : addExportOption(prefix + option)
                            }
                        /> */}
                        <RadioButton
                            isSelected={selectedExportOptions.has(prefix + option)}
                            onClick={() =>
                                selectedExportOptions.has(prefix + option)
                                    ? removeExportOption(prefix + option)
                                    : addExportOption(prefix + option)
                            }
                        />
                        <small className="text-gray-300">{option}</small>
                        {val.copyCallback && typeof val.copyCallback === "function" && (
                            <>
                                <div className="flex-grow min-w-2" />
                                <div
                                    className="text-gray-300 hover:text-gray-100 flex items-center gap-2 cursor-pointer relative"
                                    onClick={(e) => {
                                        e.stopPropagation()
                                        if (
                                            (val.restrictedLicenses as string[])?.includes(
                                                userLicense?.subscriptionType || ""
                                            ) &&
                                            !isWhitelisted
                                        ) {
                                            showLicensePaywallModal("exports")
                                            return
                                        }
                                        ;(
                                            val as {
                                                copyCallback: (exportOptionId: string) => void
                                            }
                                        ).copyCallback(prefix + option)
                                    }}
                                >
                                    {exportIdBeingCopied !== prefix + option && (
                                        <small>Copy Link</small>
                                    )}
                                    {exportIdBeingCopied !== prefix + option ? (
                                        <Icons.SmallIcon Icon={Icons.Copy} />
                                    ) : (
                                        <NovaSpinner width={20} height={20} />
                                    )}
                                </div>
                            </>
                        )}
                    </div>
                ) : (
                    <Fragment key={option}>
                        {/* Download Category */}
                        <div
                            className="text-gray-0 flex items-center py-1 select-none cursor-pointer"
                            style={{ marginLeft: `${depth}rem` }}
                            onClick={() =>
                                setCategoryIsClosed(
                                    prefix + option,
                                    !closedCategories.has(prefix + option)
                                )
                            }
                        >
                            <Icons.Icon
                                Icon={Icons.ArrowDropdownRight}
                                className={classNames(animationClass, "text-gray-0 -mt-0.5", {
                                    "rotate-90": !closedCategories.has(prefix + option),
                                })}
                            />
                            <small>{option}</small>
                        </div>

                        {/* Recursion on Children */}
                        {val && !closedCategories.has(prefix + option) && (
                            <InspectorPanelAccordionExportFileOptions
                                downloadOptions={val as NestedDownloadData}
                                depth={depth + 1}
                                prefix={prefix + option + "-"}
                                selectedExportOptions={selectedExportOptions}
                                addExportOption={addExportOption}
                                removeExportOption={removeExportOption}
                                closedCategories={closedCategories}
                                setCategoryIsClosed={setCategoryIsClosed}
                                exportIdBeingCopied={exportIdBeingCopied}
                            />
                        )}
                    </Fragment>
                )
            )}
        </div>
    )
}
